import { defaultLocale, type Locale } from '@/i18n/config'
import { PROD_APP_URL } from '@/lib/constant'
import { formatDistanceToNow } from 'date-fns'
import {
  enUS,
  zhCN,
  ja,
  es,
  fr,
  de,
  ko,
  hi,
  ar,
  bn,
  pt,
  ru,
  vi,
  it,
  tr,
  th,
} from 'date-fns/locale'

// --- Path & URL Utilities ---

/**
 * Generates a localized path, omitting the prefix for the default locale ('en').
 * This ensures that URLs for the default language are clean (e.g., /changelog)
 * while other languages are properly prefixed (e.g., /zh/changelog).
 * @param locale The current locale.
 * @param path The base path (e.g., '/changelog/my-post').
 * @returns The localized path string.
 */
export function getLocalizedPath(locale: Locale, path: string): string {
  if (locale === defaultLocale) {
    return path
  }
  // Ensure path starts with a slash for consistency
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `/${locale}${normalizedPath}`
}

// --- Date Formatting ---

/**
 * A mapping from our app's short locale codes to the standard
 * IETF BCP 47 language tags that the Intl API expects.
 * This ensures that 'zh' is correctly interpreted as Chinese (China).
 */
const localeMap: Record<Locale, string> = {
  en: 'en-US',
  zh: 'zh-CN',
  es: 'es-ES',
  ja: 'ja-JP',
  fr: 'fr-FR',
  de: 'de-DE',
  ko: 'ko-KR',
  hi: 'hi-IN',
  ar: 'ar-SA',
  bn: 'bn-BD',
  pt: 'pt-PT',
  ru: 'ru-RU',
  vi: 'vi-VN',
  it: 'it-IT',
  tr: 'tr-TR',
  th: 'th-TH',
}

export function getLocaleIetfTag(locale: Locale): string {
  return localeMap[locale] || 'en-US'
}

const dateFnsLocaleMap: Record<string, any> = {
  en: enUS,
  zh: zhCN,
  ja,
  es,
  fr,
  de,
  ko,
  hi,
  ar,
  bn,
  pt,
  ru,
  vi,
  it,
  tr,
  th,
}

/**
 * Formats a date into a relative time string (e.g., "about 1 month ago").
 * @param date The date to format.
 * @param locale The current app locale.
 * @returns The localized relative date string.
 */
export function formatRelativeDate(
  date: string | number | Date,
  locale: string,
): string {
  try {
    const dateObj = new Date(date)
    const dfnsLocale = dateFnsLocaleMap[locale] || enUS
    return formatDistanceToNow(dateObj, { addSuffix: true, locale: dfnsLocale })
  } catch (error) {
    console.error(`Error formatting relative date for locale ${locale}:`, error)
    return new Date(date).toLocaleDateString('en-US') // Fallback
  }
}

/**
 * Formats a date using the native Intl.DateTimeFormat API for a given locale.
 * This is a lightweight, zero-dependency way to handle date localization.
 * @param date The date to format (string, number, or Date object).
 * @param locale The app's locale code (e.g., 'zh').
 * @param options Intl.DateTimeFormat options. Defaults to a pleasant, readable date format.
 * @returns The formatted, localized date string.
 */
export function formatLocalizedDate(
  date: string | number | Date,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'long' },
): string {
  const dateObj = new Date(date)
  const ietfTag = getLocaleIetfTag(locale)

  try {
    return new Intl.DateTimeFormat(ietfTag, options).format(dateObj)
  } catch (error) {
    console.error(`Error formatting date for locale ${ietfTag}:`, error)
    // Fallback to a simple, non-localized format in case of an error
    return dateObj.toLocaleDateString('en-US')
  }
}

/**
 * Languages that use Right-to-Left (RTL) text direction
 */
const RTL_LANGUAGES = ['ar'] as const

/**
 * Get text direction for a given locale
 */
export function getTextDirection(locale: Locale): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.includes(locale as any) ? 'rtl' : 'ltr'
}

/**
 * Generate hreflang URLs for a given slug, enabled languages, and a base path.
 */
export function generateHreflangUrls(
  slug: string,
  enabledLanguages: string[],
  basePath: string, // e.g., '/feature' or '/changelog'
  baseUrl: string = PROD_APP_URL,
): Record<string, string> {
  const hreflangUrls: Record<string, string> = {}

  for (const lang of enabledLanguages) {
    const pathSegment = slug === '_index' ? basePath : `${basePath}/${slug}`
    hreflangUrls[lang] =
      lang === 'en'
        ? `${baseUrl}${pathSegment}`
        : `${baseUrl}/${lang}${pathSegment}`
  }

  return hreflangUrls
}

/**
 * Generate alternates object for Next.js metadata with x-default support.
 */
export function generateAlternates(
  slug: string,
  enabledLanguages: string[],
  basePath: string,
  baseUrl: string = PROD_APP_URL,
) {
  const hreflangUrls = generateHreflangUrls(
    slug,
    enabledLanguages,
    basePath,
    baseUrl,
  )

  // Add x-default pointing to the primary language (English, as specified)
  const primaryLang = 'en'

  if (enabledLanguages.includes(primaryLang)) {
    hreflangUrls['x-default'] = hreflangUrls[primaryLang]
  } else if (enabledLanguages.length > 0) {
    // Fallback if English is not available for some reason
    hreflangUrls['x-default'] = hreflangUrls[enabledLanguages[0]]
  }

  return {
    languages: hreflangUrls,
  }
}
